import React, { memo, useCallback, useMemo } from 'react';
import placeholderSrc from './placeholder.svg';
import { IconEditProfile, IconPlusCircle24 } from '@app/icons';
import { cn } from 'lib/utils';
import {
  Avatar as ShadcnAvatar,
  AvatarImage,
  AvatarFallback,
} from '../ui/avatar';
import { useNotification } from '@app/providers';

interface Props {
  src?: string;
  onChange?: (file: File | null) => void;
  value?: File | null;
  size?: number;
  borderRadius?: number;
  editable?: boolean;
  customPlaceholder?: string;
}

function Avatar(props: Props) {
  const {
    onChange,
    src = null,
    value = null,
    size = 244,
    editable = true,
    customPlaceholder,
    borderRadius = 50,
  } = props;
  const { showNotification } = useNotification();

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const { files } = e.target;

      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];

      const MAX_AVATAR_SIZE = 5 * 1024 * 1024;

      if (!file.type.startsWith('image/')) {
        showNotification({
          variant: 'error',
          message: 'Не валидный файл',
        });
        e.target.value = '';
        return;
      }

      if (file.size > MAX_AVATAR_SIZE) {
        showNotification({
          variant: 'error',
          message: 'Не валидный файл',
        });
        e.target.value = '';
        return;
      }

      onChange!(file);
    },
    [onChange, showNotification]
  );

  const imageSrc = useMemo(() => {
    if (!!value) {
      return URL.createObjectURL(value);
    }

    return src
      ? `${process.env.REACT_APP_API_URL}/api/v1/files/getById/` + src
      : customPlaceholder || placeholderSrc;
  }, [customPlaceholder, src, value]);

  return (
    <div
      className="relative w-full aspect-square max-w-full"
      style={{
        maxWidth: `${size}px`,
        maxHeight: `${size}px`,
      }}
    >
      <ShadcnAvatar
        className="w-full h-full"
        style={{ borderRadius: `${borderRadius}%` }}
      >
        <AvatarImage
          src={imageSrc}
          alt=""
          className="w-full h-full object-cover"
          style={{ borderRadius: `${borderRadius}%` }}
        />
        <AvatarFallback
          className="w-full h-full object-cover"
          style={{ borderRadius: `${borderRadius}%` }}
        >
          <img
            src={customPlaceholder || placeholderSrc}
            alt=""
            className="w-full h-full object-cover"
            style={{ borderRadius: `${borderRadius}%` }}
          />
        </AvatarFallback>
      </ShadcnAvatar>

      {!!onChange && editable && (
        <label
          className={cn(
            'inline-flex items-center justify-center rounded-full text-white cursor-pointer bg-[#3a57e8] absolute bottom-0 right-0',
            'flex-grow-0 flex-shrink-0 w-[27%] h-[27%]'
          )}
        >
          {!!src || !!value ? (
            <IconEditProfile className="w-[60%] h-[60%]" />
          ) : (
            <IconPlusCircle24 className="w-[60%] h-[60%]" />
          )}
          <input
            className="hidden"
            type="file"
            accept="image/*"
            onChange={onChangeInput}
          />
        </label>
      )}
    </div>
  );
}

export default memo(Avatar);
